import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { HANDLE_ERROR_LOCALLY_HEADER } from "../../../../../api/handleErrorLocally";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { getUploadErrorMessage } from "../../../../../upload/uploadNotifications";
import {
  AssetDependantRequest,
  AssetInventoryCompletenessSection,
  SaveAssetDependantsRequest,
} from "../../../../api/ClientFileApi";
import { useAssetInventoryApi, useOptionalAssetInventoryDocumentsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { useAssetDocumentMoves } from "../../../hooks/useAssetDocumentMoves";
import { useMissingFieldMessages } from "../../../hooks/useMissingFieldMessages";
import { usePendingMovesField } from "../../../hooks/usePendingMovesField";
import { useSaveAttempted } from "../../../hooks/useSaveAttempted";
import { describeMissingDependantField } from "../RelativesView.missingFields";
import { AssetRelativesFormValues, DependantFormValue } from "../RelativesView.types";
import { createDependantSchema } from "../RelativesView.validation";

function toRequest(dependant: DependantFormValue): AssetDependantRequest {
  const { isEntitledToMaintenance } = dependant;
  const isEntitled = isEntitledToMaintenance === true;
  const hasToPay = isEntitled && dependant.hasToPayAlimony === true;
  const paidFull = hasToPay ? dependant.paidFullLastMonth : null;

  return {
    id: dependant.id,
    firstName: dependant.firstName,
    relationship: dependant.relationship,
    birthDate: dependant.birthDate,
    livesInHousehold: dependant.livesInHousehold,
    isJointlyLiable: dependant.isJointlyLiable,
    isEntitledToMaintenance,
    hasToPayAlimony: isEntitled ? dependant.hasToPayAlimony : null,
    monthlyAlimonyAmount: hasToPay ? dependant.monthlyAlimonyAmount : null,
    paidFullLastMonth: paidFull,
    fullyGarnishedLastMonth: paidFull === false ? dependant.fullyGarnishedLastMonth : null,
    note: dependant.note,
  };
}

export function useAssetRelatives(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const clientsApi = useAssetInventoryApi();
  // Optional slice: without it the maintenance-documents block does not render, so no files can
  // ever be pending or marked for deletion here (the guards below are defensive only).
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const moveDocuments = useAssetDocumentMoves(clientId);

  const uploadDocuments = async (files: File[]): Promise<File[]> => {
    if (!documentsApi) {
      return files;
    }

    const failed: File[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append("file", file);
          await documentsApi.uploadAssetInventoryDependantDocument(
            { clientId },
            { body: formData, headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } }
          );
        } catch (error: unknown) {
          failed.push(file);
          showError(await getUploadErrorMessage(error, file.name, t));
        }
      })
    );

    void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryDependantDocuments, clientId] });
    return failed;
  };

  const deleteDocuments = async (ids: string[]): Promise<boolean> => {
    if (ids.length === 0) {
      return true;
    }
    if (!documentsApi) {
      return false;
    }

    try {
      await Promise.all(
        ids.map((documentId) => documentsApi.deleteAssetInventoryDependantDocument({ clientId, documentId }))
      );
      void queryClient.invalidateQueries({
        queryKey: [ClientFileQueryKey.AssetInventoryDependantDocuments, clientId],
      });
      return true;
    } catch {
      showError(t("clients.client.debtSettlement.assetInventory.dependants.deleteDocumentsFailed"));
      return false;
    }
  };

  const { data } = useQuery({
    queryKey: [ClientFileQueryKey.AssetInventoryDependants, clientId],
    queryFn: () => clientsApi.getAssetInventoryDependants({ clientId }),
  });

  const saveMutation = useMutation({
    mutationFn: (request: SaveAssetDependantsRequest) =>
      clientsApi.saveAssetInventoryDependants({ clientId, saveAssetDependantsRequest: request }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryDependants, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryIncome, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryExpenses, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.BankruptcyChecklist, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryCompleteness, clientId] });
    },
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        dependants: Yup.array().of(createDependantSchema(t)),
      }),
    [t]
  );

  const initialValues: AssetRelativesFormValues = useMemo(
    () => ({
      dependants: (data?.dependants ?? []).map((d) => ({
        key: d.id,
        id: d.id,
        firstName: d.firstName,
        relationship: d.relationship,
        birthDate: d.birthDate ?? null,
        livesInHousehold: d.livesInHousehold,
        isJointlyLiable: d.isJointlyLiable ?? null,
        isEntitledToMaintenance: d.isEntitledToMaintenance,
        hasToPayAlimony: d.hasToPayAlimony ?? null,
        monthlyAlimonyAmount: d.monthlyAlimonyAmount ?? null,
        paidFullLastMonth: d.paidFullLastMonth ?? null,
        fullyGarnishedLastMonth: d.fullyGarnishedLastMonth ?? null,
        note: d.note ?? null,
      })),
      documents: [],
      documentsToDelete: [],
      documentMoves: [],
    }),
    [data]
  );

  const formik = useFormikWithDirtyForm<AssetRelativesFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, helpers) => {
      const request: SaveAssetDependantsRequest = {
        dependants: values.dependants.map(toRequest),
      };
      await saveMutation.mutateAsync(request);

      const deleted = await deleteDocuments(values.documentsToDelete);
      if (deleted) {
        await helpers.setFieldValue("documentsToDelete", []);
      }

      let uploadFailed = false;
      if (values.documents.length > 0) {
        const failed = await uploadDocuments(values.documents);
        await helpers.setFieldValue("documents", failed);
        uploadFailed = failed.length > 0;
      }

      const failedMoves = await moveDocuments(values.documentMoves);
      await helpers.setFieldValue("documentMoves", failedMoves);

      if (deleted && !uploadFailed && failedMoves.length === 0) {
        showSuccess(t("clients.client.debtSettlement.assetInventory.dependants.successfullySaved"));
      }
    },
  });

  const { onAddMove, onCancelMove } = usePendingMovesField(formik);

  const saveAttempted = useSaveAttempted(formik.submitCount);

  const missingFields = useMissingFieldMessages(
    clientId,
    AssetInventoryCompletenessSection.Relatives,
    formik.values,
    describeMissingDependantField
  );

  return { formik, missingFields, saveAttempted, onAddMove, onCancelMove };
}
